import { encodePolyline, haversineKm, type GpsPosition, type RideSummary, type RideTrackPoint } from '@motogo/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';

import { useSettings } from '@/hooks/useSettings';
import { appendRidePoints } from '@/lib/rides';
import { deleteLocalRide, getUnsyncedRides, markSynced, saveLocalRide } from '@/lib/rideStore';

// Downsample gate: keep a fix once it's ≥20 m from the last kept one OR ≥2 s later (so
// stops still get periodic points). Flush to IndexedDB every N kept points so a crash /
// refresh mid-ride loses at most that many. See CLAUDE.md → Ride History & Data Tier.
const MIN_MOVE_M = 20;
const MIN_INTERVAL_MS = 2000;
const FLUSH_EVERY = 25;

// Auto-record: arm on the map, start when movement is unmistakable. 10 km/h is above
// anything a pushed bike does; two *consecutive* fixes above it filter out the single
// garbage fix a stationary phone occasionally produces. The ring buffer backfills the
// pull-away so the ride starts where movement began, not where detection triggered.
const AUTO_START_MPS = 10 / 3.6;
const AUTO_START_FIXES = 2;
const RING_BUFFER_MS = 120_000;

// A finalized recording shorter than this isn't a ride (false trigger, moved the bike
// in the garage) — discarded, including any flushed IndexedDB copy.
const MIN_RIDE_M = 500;

// Best-effort upload cadence while recording. Uploads are *appends* — only the points
// not yet acknowledged go out (~30 s ≈ a few KB), and the server dedupes on
// (ride_id, timestamp), so an ambiguous failure just re-sends the same chunk safely.
// A lost/broken phone costs at most this window. Gated on MIN_RIDE_M so discards
// never reach the server.
const UPLOAD_EVERY_MS = 30_000;

const round3 = (v: number): number => Math.round(v * 1000) / 1000;

// Per-interval telemetry accumulator. Marginal peaks (peakLongG / peakLeanDeg) feed the
// timeline traces; the sample at peak combined load (ggLongG / ggLatG) feeds the G-G — kept
// separate so the friction circle never shows a fabricated point. See CLAUDE.md.
interface TelAccumulator {
  has: boolean;
  hasLean: boolean;
  peakLongG: number;
  peakLeanDeg: number;
  ggLongG: number;
  ggLatG: number;
  ggMag: number;
}

const emptyTel = (): TelAccumulator => ({
  has: false,
  hasLean: false,
  peakLongG: 0,
  peakLeanDeg: 0,
  ggLongG: 0,
  ggLatG: 0,
  ggMag: -1,
});

interface RideRecorderValue {
  recording: boolean;
  /** Auto-record is on and waiting for movement (not currently recording). */
  armed: boolean;
  startedAtMs: number | null;
  distanceMeters: number;
  pointCount: number;
  start: () => void;
  stop: () => Promise<void>;
  /** Feed each GPS fix in (from MapView). Drives auto-start/split when idle. */
  recordPosition: (pos: GpsPosition) => void;
  /** Feed motion telemetry samples in (from the telemetry drawer). No-op when not recording. */
  sampleTelemetry: (sample: { longG: number; latG: number; leanDeg: number | null }) => void;
}

const RideRecorderContext = createContext<RideRecorderValue | null>(null);

interface RecorderState {
  recording: boolean;
  startedAtMs: number | null;
  distanceMeters: number;
  pointCount: number;
}

const IDLE: RecorderState = { recording: false, startedAtMs: null, distanceMeters: 0, pointCount: 0 };

export const RideRecorderProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<RecorderState>(IDLE);
  const { settings } = useSettings();

  // Mirrors for the GPS-driven paths (auto-start / split), which must read the current
  // values without re-creating callbacks per settings change.
  const autoRecordRef = useRef(settings.autoRecord);
  autoRecordRef.current = settings.autoRecord;
  const splitGapMsRef = useRef(settings.rideSplitGapMin * 60_000);
  splitGapMsRef.current = settings.rideSplitGapMin * 60_000;

  const recordingRef = useRef(false);
  const idRef = useRef<string | null>(null);
  const startedAtRef = useRef(0);
  const pointsRef = useRef<RideTrackPoint[]>([]);
  const lastKeptRef = useRef<{ lat: number; lon: number; t: number } | null>(null);
  const distRef = useRef(0);
  const maxSpeedRef = useRef(0);
  const lastFlushCountRef = useRef(0);
  const lastUploadRef = useRef(0);
  const telRef = useRef<TelAccumulator>(emptyTel());

  // Idle-side machinery: recent fixes for backfill, the consecutive-moving-fix counter,
  // and the manual-stop latch (a deliberate stop shouldn't auto-restart two seconds
  // later — auto-start re-arms only once the bike has actually been stationary).
  const ringRef = useRef<{ pos: GpsPosition; t: number }[]>([]);
  const movingFixesRef = useRef(0);
  const suppressedRef = useRef(false);

  const buildSummary = useCallback((): RideSummary | null => {
    const id = idRef.current;
    const pts = pointsRef.current;
    if (!id || pts.length === 0) return null;
    const startedAt = new Date(startedAtRef.current).toISOString();
    const endedAt = pts[pts.length - 1].timestamp;
    const durationSeconds = Math.max(0, (Date.parse(endedAt) - startedAtRef.current) / 1000);
    const distanceMeters = distRef.current;
    return {
      id,
      name: null,
      startedAt,
      endedAt,
      distanceMeters,
      durationSeconds,
      avgSpeedMps: durationSeconds > 0 ? distanceMeters / durationSeconds : 0,
      maxSpeedMps: maxSpeedRef.current,
      maxLeanDeg: null,
      overviewPolyline: encodePolyline(pts.map((p) => [p.lat, p.lon])),
      pointCount: pts.length,
    };
  }, []);

  // Uploads any not-yet-synced rides via the append endpoint — it creates the ride when
  // the server never saw it and fills in whatever points mid-ride appends didn't deliver
  // (per-point dedupe on (ride_id, ts)), refreshing the summary either way. Stops on the
  // first 401 (not signed in) or network error, leaving the rest for a later pass.
  // `skipId` keeps the pass away from the ride currently being recorded.
  const trySync = useCallback(async (skipId?: string) => {
    let unsynced: { summary: RideSummary; points: RideTrackPoint[] }[];
    try {
      unsynced = await getUnsyncedRides();
    } catch {
      return;
    }
    for (const { summary, points } of unsynced) {
      if (summary.id === skipId) continue;
      try {
        const saved = await appendRidePoints(summary.id, summary.name, summary.startedAt, points);
        if (!saved) break; // 401 — not signed in; retry next session
        await markSynced(summary.id);
      } catch {
        break; // network error — retry later
      }
    }
  }, []);

  const persist = useCallback(async (): Promise<void> => {
    const summary = buildSummary();
    if (!summary) return;
    try {
      await saveLocalRide(summary, pointsRef.current, false);
    } catch (e) {
      console.warn('Failed to persist ride locally:', e);
    }
  }, [buildSummary]);

  // Mid-ride best-effort delta upload: only the points past the last acknowledged index
  // go out (a few KB per 30 s chunk). An unacknowledged chunk is simply re-sent next
  // tick — the server dedupes on (ride_id, ts). IndexedDB remains the local safety net.
  const uploadedCountRef = useRef(0);
  const uploadBusyRef = useRef(false);
  const uploadDelta = useCallback(async (): Promise<void> => {
    const id = idRef.current;
    if (!id || uploadBusyRef.current || distRef.current < MIN_RIDE_M) return;
    const from = uploadedCountRef.current;
    const chunk = pointsRef.current.slice(from);
    if (chunk.length === 0) return;
    uploadBusyRef.current = true;
    try {
      const saved = await appendRidePoints(id, null, new Date(startedAtRef.current).toISOString(), chunk);
      // Only advance past what we actually sent — the ride may have grown meanwhile.
      // 401 (guest / signed out) advances nothing; the final sync handles it later.
      if (saved && idRef.current === id) uploadedCountRef.current = from + chunk.length;
    } catch {
      // offline / dead zone — expected on a ride, re-sent next interval
    } finally {
      uploadBusyRef.current = false;
    }
  }, []);

  /** Appends one fix to the active recording. `t` is the fix's own time, so backfilled
   *  ring-buffer fixes keep their original timestamps. */
  const ingest = useCallback(
    (pos: GpsPosition, t: number) => {
      const last = lastKeptRef.current;
      if (last) {
        const movedM = haversineKm(last.lat, last.lon, pos.lat, pos.lon) * 1000;
        if (movedM < MIN_MOVE_M && t - last.t < MIN_INTERVAL_MS) return; // gate
        distRef.current += movedM;
      }
      lastKeptRef.current = { lat: pos.lat, lon: pos.lon, t };

      const speedMps = pos.speed != null && !Number.isNaN(pos.speed) ? pos.speed : null;
      if (speedMps != null && speedMps > maxSpeedRef.current) maxSpeedRef.current = speedMps;

      const point: RideTrackPoint = {
        timestamp: new Date(t).toISOString(),
        lat: pos.lat,
        lon: pos.lon,
        speedMps,
        headingDeg: pos.heading != null && !Number.isNaN(pos.heading) ? pos.heading : null,
      };
      // Attach the telemetry accumulated over this interval, then reset for the next one.
      const acc = telRef.current;
      if (acc.has) {
        point.longG = round3(acc.peakLongG);
        point.leanDeg = acc.hasLean ? round3(acc.peakLeanDeg) : null;
        point.ggLongG = round3(acc.ggLongG);
        point.ggLatG = round3(acc.ggLatG);
      }
      telRef.current = emptyTel();
      pointsRef.current.push(point);

      setState((s) => ({ ...s, distanceMeters: distRef.current, pointCount: pointsRef.current.length }));

      if (pointsRef.current.length - lastFlushCountRef.current >= FLUSH_EVERY) {
        lastFlushCountRef.current = pointsRef.current.length;
        void persist();
      }
      if (t - lastUploadRef.current >= UPLOAD_EVERY_MS) {
        lastUploadRef.current = t;
        void uploadDelta();
      }
    },
    [persist, uploadDelta],
  );

  /** Begins a new recording, optionally seeded with backfilled fixes (auto-start) or a
   *  resumed ride's existing track (relaunch inside the split gap). */
  const beginRecording = useCallback(
    (opts?: {
      backfill?: { pos: GpsPosition; t: number }[];
      resume?: { summary: RideSummary; points: RideTrackPoint[] };
    }) => {
      const resume = opts?.resume;
      idRef.current = resume ? resume.summary.id : crypto.randomUUID();
      startedAtRef.current = resume ? Date.parse(resume.summary.startedAt) : Date.now();
      pointsRef.current = resume ? [...resume.points] : [];
      lastKeptRef.current = null;
      distRef.current = resume ? resume.summary.distanceMeters : 0;
      maxSpeedRef.current = resume ? resume.summary.maxSpeedMps : 0;
      lastFlushCountRef.current = pointsRef.current.length;
      lastUploadRef.current = Date.now();
      // Resumed rides re-send everything on the first delta; the server dedupes, and the
      // ack then fast-forwards the counter — cheaper than tracking server state locally.
      uploadedCountRef.current = 0;
      telRef.current = emptyTel();
      if (resume && resume.points.length > 0) {
        const lastPt = resume.points[resume.points.length - 1];
        lastKeptRef.current = { lat: lastPt.lat, lon: lastPt.lon, t: Date.parse(lastPt.timestamp) };
      }
      recordingRef.current = true;
      movingFixesRef.current = 0;

      const backfill = opts?.backfill ?? [];
      if (backfill.length > 0) {
        startedAtRef.current = backfill[0].t;
        for (const { pos, t } of backfill) ingest(pos, t);
      }
      ringRef.current = [];
      setState({
        recording: true,
        startedAtMs: startedAtRef.current,
        distanceMeters: distRef.current,
        pointCount: pointsRef.current.length,
      });
    },
    [ingest],
  );

  const start = useCallback(() => {
    suppressedRef.current = false;
    beginRecording();
  }, [beginRecording]);

  /** Ends the active recording: discard non-rides (< MIN_RIDE_M), persist + upload the
   *  rest. `endedByUser` latches auto-start off until the bike is stationary again.
   *  Summary + points are snapshotted synchronously — a new ride may begin (and reset
   *  the refs) before the awaits below run. */
  const finalize = useCallback(
    async (endedByUser: boolean) => {
      if (!recordingRef.current) return;
      recordingRef.current = false;
      const id = idRef.current;
      const summary = buildSummary();
      const points = pointsRef.current;
      setState(IDLE);
      if (endedByUser) suppressedRef.current = true;

      if (!summary || summary.distanceMeters < MIN_RIDE_M || points.length < 2) {
        // Not a ride — false trigger or a shuffle around the garage. Mid-ride uploads
        // were gated on MIN_RIDE_M, but a flushed local copy may exist; remove it.
        if (id) await deleteLocalRide(id).catch(() => {});
        return;
      }
      try {
        await saveLocalRide(summary, points, false);
      } catch (e) {
        console.warn('Failed to persist ride locally:', e);
      }
      await trySync();
    },
    [buildSummary, trySync],
  );

  const stop = useCallback(() => finalize(true), [finalize]);

  const recordPosition = useCallback(
    (pos: GpsPosition) => {
      const now = Date.now();
      const moving = pos.speed != null && !Number.isNaN(pos.speed) && pos.speed > AUTO_START_MPS;

      if (recordingRef.current) {
        // Split: a gap longer than the setting means the previous ride ended at its
        // last fix (screen locked at lunch, app killed overnight). Finalize it and fall
        // through to the idle path so this fix starts arming the next ride.
        const last = lastKeptRef.current;
        if (last && now - last.t > splitGapMsRef.current) {
          void finalize(false);
        } else {
          ingest(pos, now);
          return;
        }
      }

      // Idle path: maintain the backfill ring buffer and watch for sustained movement.
      const ring = ringRef.current;
      ring.push({ pos, t: now });
      while (ring.length > 0 && now - ring[0].t > RING_BUFFER_MS) ring.shift();

      if (!moving) {
        movingFixesRef.current = 0;
        suppressedRef.current = false; // stationary again — a manual stop stops suppressing
        return;
      }
      if (!autoRecordRef.current || suppressedRef.current) return;

      movingFixesRef.current += 1;
      if (movingFixesRef.current < AUTO_START_FIXES) return;

      // Sustained movement — start from where the pull-away began: the trailing run of
      // moving fixes plus one stationary fix before it (the actual departure point).
      let from = ring.length - 1;
      while (from > 0) {
        const s = ring[from - 1].pos.speed;
        if (s == null || Number.isNaN(s) || s <= AUTO_START_MPS) break;
        from -= 1;
      }
      if (from > 0) from -= 1;
      beginRecording({ backfill: ring.slice(from) });
    },
    [beginRecording, finalize, ingest],
  );

  const sampleTelemetry = useCallback((sample: { longG: number; latG: number; leanDeg: number | null }) => {
    if (!recordingRef.current) return;
    const acc = telRef.current;
    acc.has = true;
    if (Math.abs(sample.longG) > Math.abs(acc.peakLongG)) acc.peakLongG = sample.longG;
    if (sample.leanDeg != null && (!acc.hasLean || Math.abs(sample.leanDeg) > Math.abs(acc.peakLeanDeg))) {
      acc.hasLean = true;
      acc.peakLeanDeg = sample.leanDeg;
    }
    // Track the sample at peak combined load for the G-G (real co-occurring pair).
    const mag = sample.longG * sample.longG + sample.latG * sample.latG;
    if (mag > acc.ggMag) {
      acc.ggMag = mag;
      acc.ggLongG = sample.longG;
      acc.ggLatG = sample.latG;
    }
  }, []);

  // On mount: adopt a dangling ride (crash / killed app / relaunch at the fuel stop) if
  // its last point is inside the split gap — the ride simply continues. Anything older
  // is already final (its end was its last fix all along); upload those. Lazy finalize:
  // rides end retroactively, whenever we next get the chance to notice.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let resumeId: string | undefined;
      try {
        const unsynced = await getUnsyncedRides();
        const gapMs = splitGapMsRef.current;
        const now = Date.now();
        const candidate = unsynced
          .filter(({ points }) => points.length > 0)
          .map((r) => ({ ...r, lastT: Date.parse(r.points[r.points.length - 1].timestamp) }))
          .sort((a, b) => b.lastT - a.lastT)[0];
        if (candidate && now - candidate.lastT <= gapMs && !cancelled && !recordingRef.current) {
          resumeId = candidate.summary.id;
          beginRecording({ resume: { summary: candidate.summary, points: candidate.points } });
        }
      } catch {
        // store hiccup — sync pass below still runs
      }
      await trySync(resumeId);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recorded rides must survive browser storage pressure — ask for durable storage once.
  useEffect(() => {
    if (navigator.storage?.persist) void navigator.storage.persist().catch(() => {});
  }, []);

  const armed = !state.recording && settings.autoRecord;

  const value = useMemo<RideRecorderValue>(
    () => ({
      recording: state.recording,
      armed,
      startedAtMs: state.startedAtMs,
      distanceMeters: state.distanceMeters,
      pointCount: state.pointCount,
      start,
      stop,
      recordPosition,
      sampleTelemetry,
    }),
    [state, armed, start, stop, recordPosition, sampleTelemetry],
  );

  return <RideRecorderContext.Provider value={value}>{children}</RideRecorderContext.Provider>;
};

export function useRideRecorder(): RideRecorderValue {
  const ctx = useContext(RideRecorderContext);
  if (!ctx) {
    throw new Error('useRideRecorder must be used within a RideRecorderProvider');
  }
  return ctx;
}
